import { chunkStampsOf, type AssetManifest } from "daedalus-core";
import { pageTextureKey } from "./atlas.ts";

/**
 * Turns one chunk's packed cells into the stamps that paint its texture (ADR-019 item 5), on top of the
 * pure iterator in `daedalus-core`. One stamp record is reused for every callback so painting a chunk
 * allocates nothing per cell.
 */

/**
 * A chunk's cells and what they are packed against. `missing`, when given, is told the index of each cell whose
 * frame the manifest lacks, and that cell is not stamped; without it such a cell is an error.
 */
export interface ChunkCells {
  readonly tiles: Uint32Array;
  readonly chunkSize: number;
  readonly tileSize: number;
  readonly tilesets: readonly string[];
  readonly variant: string | null;
  readonly manifest: AssetManifest;
  readonly missing?: ((cell: number) => void) | undefined;
}

/**
 * One frame to stamp at a pixel position inside the chunk texture, mirrored as its cell is (ADR-089).
 */
export interface Stamp {
  texture: string;
  frame: string;
  x: number;
  y: number;
  height: number;
  flipX: boolean;
  flipY: boolean;
}

/**
 * A chunk's collected stamps, reused across paints: `stamps` holds `count` records and grows to the
 * fullest chunk seen, `rows` carries one flag per chunk row, set for a row any stamp draws into.
 */
export interface StampBuffer {
  readonly stamps: Stamp[];
  readonly rows: Uint8Array;
  count: number;
}

/**
 * Calls `emit` once per non-empty cell, in row-major order.
 *
 * @param cells The chunk.
 * @param emit Receives the reused stamp record; copy it if it must outlive the call.
 * @throws Error when the cell count is not `chunkSize * chunkSize`, a cell names a tileset index outside
 *   the table, or the manifest lacks the frame and the chunk has no `missing`.
 */
export function chunkStamps(cells: ChunkCells, emit: (stamp: Stamp) => void): void {
  const stamp: Stamp = { texture: "", frame: "", x: 0, y: 0, height: 0, flipX: false, flipY: false };
  chunkStampsOf(cells, (page, frame, rect, x, y, flipX, flipY) => {
    stamp.texture = pageTextureKey(cells.manifest, page);
    stamp.frame = frame;
    stamp.x = x;
    stamp.y = y;
    stamp.height = rect.h;
    stamp.flipX = flipX;
    stamp.flipY = flipY;
    emit(stamp);
  });
}

/**
 * A buffer for the stamps of a chunk of `chunkSize` by `chunkSize` cells.
 *
 * @param chunkSize Chunk size in tiles.
 * @returns An empty buffer.
 */
export function createStampBuffer(chunkSize: number): StampBuffer {
  return { stamps: [], rows: new Uint8Array(chunkSize), count: 0 };
}

/**
 * Collects a chunk's stamps into a buffer and marks the rows they draw into, so a caller knows what the
 * chunk holds before it allocates anything for it. A stamp marks every row its frame covers, since a frame
 * taller than a tile draws on down from its own row.
 *
 * @param cells The chunk.
 * @param buffer Buffer to fill; its records and row flags are overwritten.
 * @throws Error when the cell count is not `chunkSize * chunkSize`, a cell names a tileset index outside
 *   the table, or the manifest lacks the frame and the chunk has no `missing`; the buffer is then left part-filled and must be refilled
 *   before it is read.
 */
export function collectStamps(cells: ChunkCells, buffer: StampBuffer): void {
  const { stamps, rows } = buffer;
  buffer.count = 0;
  rows.fill(0);
  chunkStamps(cells, (stamp) => {
    const record = stamps[buffer.count];
    if (record === undefined) {
      stamps.push({ ...stamp });
    } else {
      record.texture = stamp.texture;
      record.frame = stamp.frame;
      record.x = stamp.x;
      record.y = stamp.y;
      record.height = stamp.height;
      record.flipX = stamp.flipX;
      record.flipY = stamp.flipY;
    }
    buffer.count += 1;
    const last = Math.min(rows.length - 1, Math.floor((stamp.y + Math.max(stamp.height, 1) - 1) / cells.tileSize));
    for (let row = Math.floor(stamp.y / cells.tileSize); row <= last; row += 1) {
      rows[row] = 1;
    }
  });
}

/**
 * How a stamp is drawn into a chunk texture: from its cell's top-left, mirrored by a negative scale about the
 * far edge so a mirrored frame covers the same rectangle as an unmirrored one (ADR-089).
 */
export interface StampDraw {
  readonly originX: number;
  readonly originY: number;
  readonly scaleX: number;
  readonly scaleY: number;
}

const DRAWS: readonly StampDraw[] = [
  { originX: 0, originY: 0, scaleX: 1, scaleY: 1 },
  { originX: 1, originY: 0, scaleX: -1, scaleY: 1 },
  { originX: 0, originY: 1, scaleX: 1, scaleY: -1 },
  { originX: 1, originY: 1, scaleX: -1, scaleY: -1 },
];

/**
 * The draw settings for a stamp's mirroring, shared and never allocated per stamp.
 *
 * @param stamp The stamp.
 * @returns Its origin and scale.
 */
export function stampConfig(stamp: { readonly flipX: boolean; readonly flipY: boolean }): StampDraw {
  return DRAWS[(stamp.flipX ? 1 : 0) + (stamp.flipY ? 2 : 0)] as StampDraw;
}
